import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authClient } from './authClient';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export type SessionStatus = 'loading' | 'signed_in' | 'signed_out';

export interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  offline: boolean;
  refresh(): Promise<void>;
  /** Best effort on the server; always ends the session on this device. */
  signOut(): Promise<void>;
  /** "Sign out of all devices": revokes every session on the server. Throws when offline. */
  revokeAllSessions(): Promise<void>;
}

const CACHE_KEY = 'tahi.session';

export function readCachedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

export function writeCachedUser(user: SessionUser | null): void {
  try {
    if (user) localStorage.setItem(CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    // storage blocked: offline launches will show sign-in
  }
}

/**
 * Asks the server who is signed in. Offline (or on a server error) it falls back to the last
 * signed-in user cached on this device; only an explicit "signed out" answer clears that cache.
 */
export async function resolveSession(): Promise<{ user: SessionUser | null; offline: boolean }> {
  try {
    const { data, error } = await authClient.getSession();
    if (error) {
      if ((error.status ?? 0) >= 500) return { user: readCachedUser(), offline: true };
      writeCachedUser(null);
      return { user: null, offline: false };
    }
    const u = data?.user;
    const user = u ? { id: u.id, email: u.email, name: u.name } : null;
    writeCachedUser(user);
    return { user, offline: false };
  } catch {
    return { user: readCachedUser(), offline: true };
  }
}

export const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    status: SessionStatus;
    user: SessionUser | null;
    offline: boolean;
  }>({ status: 'loading', user: null, offline: false });

  const refresh = useCallback(async () => {
    const r = await resolveSession();
    setState({ status: r.user ? 'signed_in' : 'signed_out', user: r.user, offline: r.offline });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await authClient.signOut();
    } catch {
      // Offline: the session cookie expires on its own; this device is signed out now.
    } finally {
      writeCachedUser(null);
      setState({ status: 'signed_out', user: null, offline: false });
    }
  }, []);

  const revokeAllSessions = useCallback(async () => {
    const { error } = await authClient.revokeSessions();
    if (error) throw new Error(error.message ?? 'Could not sign out other devices');
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ ...state, refresh, signOut, revokeAllSessions }),
    [state, refresh, signOut, revokeAllSessions],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}
